import { useState, useEffect, useRef } from "react";

export function useReveal(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  return { ref, visible };
}

export function useCountdown(targetTimestamp) {
  const [diff, setDiff] = useState(targetTimestamp - Date.now());

  useEffect(() => {
    const id = setInterval(() => setDiff(targetTimestamp - Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetTimestamp]);

  if (diff <= 0) return { days: "00", hours: "00", mins: "00", secs: "00" };

  const pad = (n) => String(n).padStart(2, "0");
  return {
    days: pad(Math.floor(diff / 864e5)),
    hours: pad(Math.floor((diff % 864e5) / 36e5)),
    mins: pad(Math.floor((diff % 36e5) / 6e4)),
    secs: pad(Math.floor((diff % 6e4) / 1e3)),
  };
}

export function useScrolled(threshold = 40) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [threshold]);
  return scrolled;
}

export function useHover() {
  const [hovered, setHovered] = useState(false);
  return {
    hovered,
    hoverProps: {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    },
  };
}
